import React, { useState } from 'react'
import videojs from 'video.js'
import 'video.js/dist/video-js.css'
import '@videojs/themes/dist/fantasy/index.css'
import qualityLevels from 'videojs-contrib-quality-levels'
import hlsQualitySelector from 'videojs-hls-quality-selector'
import FullScreenButton from './controlBarComponents/fullScreenButton'
import VolumeBar from './controlBarComponents/volumeBar'
import OnAirLogo from '../../assets/channel-on-air-logo.png'

const IptvPlayerVideoJs = (props) => {
  const videoRef = React.useRef(null)
  const playerRef = React.useRef(null)
  const {
    options,
    onReady,
    cover,
    watermarkLogo,
    muted,
    isAudioFeed,
    channelThumbnail,
    pageData,
    volume,
  } = props
  const [toggleMute, setToggleMute] = useState(muted)
  const [isFullScreen, setIsFullScreen] = useState(false)

  const volumeData = { ...pageData?.volume, visible: true }
  const fullScreenData = pageData?.full_screen
  const channelLogoData = pageData?.channel_logo
  const onAirLogoData = pageData?.on_air_logo

  React.useEffect(() => {
    videojs.registerPlugin('qualityLevels', qualityLevels)
    videojs.registerPlugin('hlsQualitySelector', hlsQualitySelector)
    // make sure Video.js player is only initialized once
    if (!playerRef.current) {
      const videoElement = videoRef.current
      if (!videoElement) return
      const player = (playerRef.current = videojs(videoElement, options, () => {
        onReady && onReady(player)
      }))
    } else {
      // you can update player here [update player through props]
      // const player = playerRef.current;
      // player.autoplay(options.autoplay);
      // player.src(options.sources);
    }
  }, [options, videoRef])

  // Dispose the Video.js player when the functional component unmounts
  React.useEffect(() => {
    const player = playerRef.current

    return () => {
      if (player) {
        player.dispose()
        playerRef.current = null
      }
    }
  }, [playerRef])

  const fullScreenToggle = () => {
    if (!playerRef.current) {
      return
    }
    if (playerRef.current.isFullscreen()) {
      playerRef.current.exitFullscreen()
      setIsFullScreen(false)
      return
    }
    setIsFullScreen(true)
    playerRef.current.requestFullscreen()
  }

  const toggleVolume = (value) => {
    if (!playerRef.current) {
      return
    }
    setToggleMute(false)
    playerRef.current.muted(false)
    playerRef.current.volume(value / 100)
  }

  const toggleMuted = (status) => {
    if (!playerRef.current) {
      return
    }
    playerRef.current.volume(status ? 0 : 100)
    playerRef.current.muted(status)
    setToggleMute(status)
  }

  let coverCss = cover ? { objectFit: 'cover' } : { objectFit: '' }

  const logoPositionConfig = {
    TOP_LEFT: 'left-[5.5px] top-[10px]',
    TOP_RIGHT: 'right-[5.5px] top-[10px]',
  }
  const onAirPositionConfig = {
    TOP_LEFT: 'left-[20px] top-[20px]',
    TOP_RIGHT: 'right-[20px] top-[20px]',
    BOTTOM_LEFT: 'left-[20px] bottom-[20px]',
    BOTTOM_RIGHT: 'right-[20px] bottom-[20px]',
  }

  return (
    <div data-vjs-player="" className="relative">
      <div
        style={{ zIndex: 40 }}
        className=" absolute w-full h-[calc(100%-40px)] lg:h-[calc(100%-50px)] top-0 right-0"
      >
        {channelLogoData?.visible ? (
          <img
            src={watermarkLogo}
            className={`absolute w-[52.3px] md:w-[49.4px] xl:w-[79px] h-[29.4px] md:h-[27.8px] xl:h-[45px] ${
              logoPositionConfig[channelLogoData?.position]
            }`}
            alt="poster"
          />
        ) : null}
        {onAirLogoData?.visible ? (
          <img
            src={OnAirLogo}
            className={`absolute w-[31px] md:w-[29.3px] xl:w-[46.9px] ${
              onAirPositionConfig[onAirLogoData?.position]
            }`}
            alt="poster"
          />
        ) : null}
      </div>

      {/* Display channel thumbnail for audio feeds */}
      {isAudioFeed && channelThumbnail && (
        <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 1 }}>
          <img
            src={channelThumbnail}
            alt="Channel Thumbnail"
            className="w-full h-full object-contain"
            style={{ maxWidth: '100%', maxHeight: '100%' }}
          />
        </div>
      )}

      <div
        className="flex items-center justify-end absolute right-0 bottom-0 w-full z-10 h-10 lg:h-[50px] px-3 lg:px-5"
        style={{ background: 'linear-gradient(to top, rgba(0, 0, 0), rgba(0, 0, 0,0.1))' }}
      >
        <div
          style={{ background: '#000' }}
          className=" h-[20px] lg:h-[26px] rounded-full flex justify-center items-center "
        >
          <VolumeBar
            toggleMuted={toggleMuted}
            volume={volume}
            toggleVolume={toggleVolume}
            isMute={toggleMute}
            playerData={{
              control_bar: {
                data: {
                  left_control_buttons: {
                    volume: volumeData,
                  },
                },
              },
            }}
          />
        </div>

        <div
          style={{ background: '#000' }}
          className="w-[20px] h-[20px] lg:h-[26px] lg:w-[26px] rounded-full flex justify-center items-center p-[2.5px] lg:p-1 ml-3"
        >
          <FullScreenButton
            data={{ ...fullScreenData, visible: true }}
            isFullScreen={isFullScreen}
            toggleFullScreen={fullScreenToggle}
          />
        </div>
      </div>

      <video
        ref={videoRef}
        style={coverCss}
        className="video-js vjs-fill vjs-big-play-centered object-cove vjs-theme-fantasy"
      />
    </div>
  )
}

export default IptvPlayerVideoJs
